import get from 'lodash/get';
import * as types from '../actionTypes/SavedListActionTypes';

const getMinPrice = (item) => {
    const amounts = get(item, 'pricing.amounts', []);
    const prices = amounts.reduce((aggregate, amount) => {
        const localAggregate = aggregate;
        localAggregate[amount.type] = amount.min;
        return localAggregate;
    }, {});
    return (
        prices.CLEARANCE ||
        prices.SALE ||
        prices.NOW ||
        prices.ORIGINAL ||
        get(amounts, '[0].min', 0)
    );
};

const priceComparator = (order, itemA, itemB) => {
    const couponPriceA = get(itemA, 'pricing.couponInfo[0].amount');
    const couponPriceB = get(itemB, 'pricing.couponInfo[0].amount');
    const priceA = couponPriceA ? couponPriceA.min : getMinPrice(itemA);
    const priceB = couponPriceB ? couponPriceB.min : getMinPrice(itemB);
    return order * (priceA - priceB);
};

const doSortItems = (items, sort) => {
    let comparator;
    switch (sort) {
        case 'date':
            comparator = ({ dateAdded: dateA }, { dateAdded: dateB }) => {
                const date1 = new Date(dateA);
                const date2 = new Date(dateB);
                return date2.getTime() - date1.getTime();
            };
            break;
        case 'priceDesc':
            comparator = priceComparator.bind(null, -1);
            break;
        case 'priceAsc':
            comparator = priceComparator.bind(null, 1);
            break;
        case 'rating':
            comparator = ({ reviews: reviewsA }, { reviews: reviewsB }) => {
                const ratingA = get(reviewsA, 'rating', 0);
                const ratingB = get(reviewsB, 'rating', 0);
                return ratingB - ratingA;
            };
            break;
        default:
            comparator = ({ mostWanted: m1 = false }, { mostWanted: m2 = false }) =>
                (m2 ? 1 : 0) - (m1 ? 1 : 0);
    }
    items.sort(comparator);
};

const doSortItemsSavedList = (savedList, sort) => {
    const { data } = savedList;
    const { items = [] } = data || {};
    const newItems = [...items];
    doSortItems(newItems, sort);
    return {
        savedList: {
            ...savedList,
            data: {
                ...data,
                items: newItems,
            },
        },
    };
};

function SavedListReducer(
    state = { listItems: { listData: [] }, isLoaded: false, saveToList: {} },
    action = {}
) {
    // NOSONAR
    switch (action.type) {
        case types.DELETE_SAVED_LIST_SUCCESS: {
            const newState = { ...state };
            newState.deleteList = action.list;
            return newState;
        }
        case types.DELETE_SAVED_LIST_ERROR: {
            return { ...state, deleteList: action };
        }
        case types.GET_SAVED_LIST: {
            return {
                ...state,
                savedList: { error: {}, isLoaded: false, data: [], statusCode: '' },
            };
        }
        case types.GET_SAVED_LIST_ERROR: {
            const isLoaded = get(action, 'savedList.isLoaded', true);
            return { ...state, savedList: { error: action.error, isLoaded } };
        }
        case types.REMOVE_SAVED_LIST_ITEM: {
            const { uuid } = action.payload;
            const items = get(state, 'savedList.data.items', []);
            const itemIndex = items.findIndex((item) => item.id === uuid);
            if (itemIndex !== -1) {
                const newItems = items.filter((item, index) => itemIndex !== index);
                const { savedList } = state;
                const { data } = savedList;
                return {
                    ...state,
                    savedList: {
                        ...savedList,
                        data: {
                            ...data,
                            items: newItems,
                        },
                    },
                };
            }
            return state;
        }
        case types.GET_SAVED_LIST_SUCCESS: {
            const data = action.savedList;
            if (action.savedList.isGuestMode) {
                const list = get(data, 'response.data.items', []);
                doSortItems(list, 'priority');
            }
            const savedList = {
                data: data.response.data,
                statusCode: data.response.status,
                isLoaded: action.savedList.isLoaded,
                isGuestMode: action.savedList.isGuestMode,
                error: {},
            };
            const deleteList = {};
            const toggleMostWanted = {};
            return { ...state, savedList, deleteList, toggleMostWanted };
        }
        case types.UPDATE_SORT_SAVED_ITEMS: {
            const newSavedList = doSortItemsSavedList(state.savedList, action.payload.sort);
            return {
                ...state,
                ...newSavedList,
            };
        }
        case types.GET_LIST_CATEGORY_SUCCESS: {
            let sortList = get(action, 'payload.sortList', {});
            if (sortList.isGuestMode) {
                const newSavedList = doSortItemsSavedList(
                    sortList,
                    get(action, 'payload.sort', 'priority')
                );
                sortList = newSavedList.savedList;
            }
            return { ...state, savedList: sortList }; // wen need to override
            // with the new sortedList
        }
        case types.UPDATE_MOST_WANTED_IN_STORE: {
            const isGuestMode = get(state, 'savedList.isGuestMode', false);
            if (isGuestMode) {
                const { uuid } = action.payload;
                const items = get(state, 'savedList.data.items', []);
                const itemIndex = items.findIndex((item) => item.id === uuid);
                if (itemIndex !== -1) {
                    const newItems = items.map((item, index) => {
                        if (itemIndex === index) {
                            return {
                                ...item,
                                mostWanted: action.payload.mostWanted,
                            };
                        }
                        return item;
                    });
                    const { savedList } = state;
                    const { data } = savedList;
                    return {
                        ...state,
                        savedList: {
                            ...savedList,
                            data: {
                                ...data,
                                items: newItems,
                            },
                        },
                    };
                }
            }
            return state;
        }
        case types.SHARE_LIST: {
            const shareList = { isLoading: true };
            return { ...state, shareList };
        }
        case types.SHARE_LIST_RESET: {
            const shareList = {};
            return { ...state, shareList };
        }
        case types.SHARE_LIST_SUCCESS: {
            const { response } = action;
            const shareList = { response, isLoading: false };
            return { ...state, shareList };
        }
        case types.SHARE_LIST_ERROR: {
            const shareList = { ...action.errorResponse, isLoading: false };
            return { ...state, shareList };
        }
        case types.TOGGLE_MOST_WANTED_IN_LIST_SUCCESS: {
            const toggleMostWanted = action.response;
            return { ...state, toggleMostWanted };
        }

        case types.UPDATE_SAVED_LIST_SUCCESS: {
            const saveToList = action.updateList;
            const { savedList } = state;
            savedList.data.name = saveToList.listName;
            savedList.data.isPublic = saveToList.isPublic;
            savedList.data.eventDate = saveToList.eventDate;
            return { ...state, saveToList, savedList };
        }

        case types.GET_SAVED_LIST_GUEST: {
            const savedList = {
                data: {
                    id: 'myFavorites',
                    name: 'My Favorites',
                    isPublic: false,
                    totalItems: 1,
                    totalPages: 1,
                    currentPageNumber: 1,
                    limit: 50,
                    items: [],
                },
                isSuccess: true,
                statusCode: 200,
                isLoaded: true,
                isGuestMode: true,
            };
            return { ...state, savedList };
        }

        default:
            return state;
    }
}

SavedListReducer.reducerName = 'savedList';
export default SavedListReducer;
